import React, { useState, useEffect, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  getInventoryPurchases,
  getInventoryPurchasesSummary,
  createInventoryPurchase,
  updateInventoryPurchase,
  deleteInventoryPurchase,
  getSuppliers,
  createSupplier,
  updateSupplier,
  mergeSuppliers,
  deleteSupplier,
  migrateSuppliers,
} from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDualCurrency, formatDate, toLocalDateString } from '../utils/formatters';
import { useExchangeRate } from '../contexts/ExchangeRateContext';

const Inventory = () => {
  const { 
    stores, 
    loading: storesLoading, 
    error: storesError, 
    setError: setStoresError,
    updateStore, 
    deleteStore,
    getStoreName
  } = useStores();
  const { exchangeRate } = useExchangeRate();
  const { user } = useAuth();
  
  // Check if user is compras only
  const isComprasOnly = user?.role === 'compras';

  const PAGE_SIZE = 50;
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState([]); // Aggregated per-store totals for summary cards
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [storeFormData, setStoreFormData] = useState({
    name: '',
    location: '',
    description: '',
    monthly_goal: '',
  });
  const [formData, setFormData] = useState({
    store_id: '',
    supplier: '',
    invoice_number: '',
    quantity: '',
    reception_date: new Date(),
    credit_days: '',
    total_amount_usd: '',
    total_amount_ves_original: '',
    exchange_rate_at_purchase: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    storeId: '',
    startDate: null,
    endDate: null,
    invoiceNumber: '',
  });
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState('');
  const invoiceSearchTimer = useRef(null);
  // suppliers: [{id, name, aliases, credit_days, discount_pct}]
  const [suppliers, setSuppliers] = useState([]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null); // objeto supplier
  const [mergeTarget, setMergeTarget] = useState('');             // nombre canónico destino
  // Modal para agregar proveedor nuevo
  const [showNewSupplierForm, setShowNewSupplierForm] = useState(false);
  const [newSupplierData, setNewSupplierData] = useState({ name: '', aliases: '', credit_days: 0, discount_pct: 0 });
  const [newSupplierError, setNewSupplierError] = useState('');
  // Modal edición de alias de un proveedor
  const [editingAliases, setEditingAliases] = useState(null);    // supplier object
  const [aliasesInput, setAliasesInput] = useState('');
  const [migrating, setMigrating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setPage(1);
    try {
      const data = await getInventoryPurchases(filters);
      setPurchases(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error al cargar las compras: ' + err.message);
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const data = await getInventoryPurchasesSummary();
      setSummary(data);
    } catch (err) {
      console.error('Error fetching inventory summary:', err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSummary();
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchData();
    }
  }, [filters]);

  const handleOpenModal = (purchase = null) => {
    if (purchase) {
      const originalDate = new Date(purchase.reception_date + 'T00:00:00');
      
      setEditingPurchase(purchase);
      setFormData({
        store_id: purchase.store_id,
        supplier: purchase.supplier,
        invoice_number: purchase.invoice_number,
        quantity: purchase.quantity.toString(),
        reception_date: originalDate,
        credit_days: purchase.credit_days.toString(),
        total_amount_usd: purchase.total_amount_usd?.toString() || '',
        total_amount_ves_original: purchase.total_amount_ves_original?.toString() || '',
        exchange_rate_at_purchase: purchase.exchange_rate_at_purchase?.toString() || exchangeRate.toString(),
      });
    } else {
      setEditingPurchase(null);
      setFormData({
        store_id: '',
        supplier: '',
        invoice_number: '',
        quantity: '',
        reception_date: new Date(),
        credit_days: '',
        total_amount_usd: '',
        total_amount_ves_original: '',
        exchange_rate_at_purchase: exchangeRate.toString(),
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPurchase(null);
    setFormData({
      store_id: '',
      supplier: '',
      invoice_number: '',
      quantity: '',
      reception_date: new Date(),
      credit_days: '',
      total_amount_usd: '',
      total_amount_ves_original: '',
      exchange_rate_at_purchase: exchangeRate.toString(),
    });
  };

  // Handle USD amount change and auto-convert to VES
  const handleUSDAmountChange = (value) => {
    const usdAmount = parseFloat(value) || 0;
    const rate = parseFloat(formData.exchange_rate_at_purchase) || exchangeRate;
    const vesAmount = (usdAmount * rate).toFixed(2);
    setFormData(prev => ({
      ...prev,
      total_amount_usd: value,
      total_amount_ves_original: vesAmount
    }));
  };

  // Handle VES amount change and auto-convert to USD
  const handleVESAmountChange = (value) => {
    const vesAmount = parseFloat(value) || 0;
    const rate = parseFloat(formData.exchange_rate_at_purchase) || exchangeRate;
    const usdAmount = (vesAmount / rate).toFixed(2);
    setFormData(prev => ({
      ...prev,
      total_amount_ves_original: value,
      total_amount_usd: usdAmount
    }));
  };

  // Handle exchange rate change and update VES amount
  const handleExchangeRateChange = (value) => {
    const rate = parseFloat(value) || 0;
    const usdAmount = parseFloat(formData.total_amount_usd) || 0;
    const vesAmount = (usdAmount * rate).toFixed(2);
    setFormData(prev => ({
      ...prev,
      exchange_rate_at_purchase: value,
      total_amount_ves_original: vesAmount
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const purchaseData = {
        store_id: formData.store_id,
        supplier: formData.supplier,
        invoice_number: formData.invoice_number,
        quantity: parseInt(formData.quantity),
        credit_days: parseInt(formData.credit_days),
        total_amount_usd: parseFloat(formData.total_amount_usd),
        total_amount_ves_original: parseFloat(formData.total_amount_ves_original),
        exchange_rate_at_purchase: parseFloat(formData.exchange_rate_at_purchase),
        reception_date: toLocalDateString(formData.reception_date),
      };

      if (editingPurchase) {
        await updateInventoryPurchase(editingPurchase.id, purchaseData);
      } else {
        await createInventoryPurchase(purchaseData);
      }

      await fetchData();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving inventory purchase:', err);
      // Show specific error message from backend if available
      const errorMessage = err.response?.data?.detail || 
        (editingPurchase ? 'Error al actualizar la compra' : 'Error al registrar la compra');
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (purchaseId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta compra de inventario?')) {
      return;
    }

    try {
      await deleteInventoryPurchase(purchaseId);
      await fetchData();
    } catch (err) {
      console.error('Error deleting inventory purchase:', err);
      setError('Error al eliminar la compra');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      storeId: '',
      startDate: null,
      endDate: null,
      invoiceNumber: '',
    });
  };

  const handleOpenStoreModal = (store = null) => {
    if (store) {
      setEditingStore(store);
      setStoreFormData({
        name: store.name,
        location: store.location,
        description: store.description || '',
        monthly_goal: store.monthly_goal || '',
      });
    } else {
      setEditingStore(null);
      setStoreFormData({
        name: '',
        location: '',
        description: '',
        monthly_goal: '',
      });
    }
    setShowStoreModal(true);
  };

  const handleCloseStoreModal = () => {
    setShowStoreModal(false);
    setEditingStore(null);
    setStoreFormData({
      name: '',
      location: '',
      description: '',
      monthly_goal: '',
    });
  };

  const handleStoreSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const storeData = {
        ...storeFormData,
        monthly_goal: storeFormData.monthly_goal ? parseFloat(storeFormData.monthly_goal) : null,
      };

      if (editingStore) {
        await updateStore(editingStore.id, storeData);
      }

      handleCloseStoreModal();
      // No need to fetch data as the context will update automatically
    } catch (err) {
      console.error('Error saving store:', err);
      setError('Error al actualizar la tienda');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStore = async (storeId, storeName) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la tienda "${storeName}"? Esta acción también eliminará todas las compras de inventario asociadas.`)) {
      return;
    }

    try {
      await deleteStore(storeId);
      await fetchData(); // Refresh inventory data
    } catch (err) {
      console.error('Error deleting store:', err);
      
      // If the store was already deleted (404), just refresh the data
      if (err.response && err.response.status === 404) {
        console.log('Store was already deleted, refreshing data...');
        await fetchData();
        setError('La tienda ya había sido eliminada. Datos actualizados.');
      } else {
        setError('Error al eliminar la tienda: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleStoreInputChange = (e) => {
    const { name, value } = e.target;
    setStoreFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Filter purchases by invoice number search (client-side fallback)
  const filteredPurchases = useMemo(() => {
    if (!searchInvoiceNumber.trim()) {
      return purchases;
    }
    return purchases.filter(purchase =>
      purchase.invoice_number?.toLowerCase().includes(searchInvoiceNumber.toLowerCase())
    );
  }, [purchases, searchInvoiceNumber]);

  const handleInvoiceNumberChange = (value) => {
    setSearchInvoiceNumber(value);
    if (invoiceSearchTimer.current) clearTimeout(invoiceSearchTimer.current);
    invoiceSearchTimer.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, invoiceNumber: value }));
    }, 500);
  };

  const totalInventoryCost = filteredPurchases.reduce((sum, purchase) => sum + (purchase.total_amount_usd || 0), 0);
  const totalItems = filteredPurchases.reduce((sum, purchase) => sum + purchase.quantity, 0);
  const totalPages = Math.max(1, Math.ceil(filteredPurchases.length / PAGE_SIZE));
  const pagedPurchases = filteredPurchases.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Build summary cards from aggregation data (no record limit)
  const purchasesByStore = summary.reduce((acc, row) => {
    const storeId = row.store_id;
    if (storeId) {
      acc[storeId] = {
        storeName: getStoreName(storeId),
        totalCost: row.total_cost_usd || 0,
        totalUnits: row.total_units || 0,
        purchasesCount: row.purchases_count || 0,
      };
    }
    return acc;
  }, {});

  if (loading || storesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Cargando inventario...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Compras de Inventario</h1>
          <p className="text-gray-600 mt-1">
            {purchases.length === 200 ? 'Últimas 200 compras' : `${purchases.length} compras`} • Total: {formatCurrency(totalInventoryCost)} • {totalItems} items
          </p>
        </div>
        <div className="flex space-x-3">
          <button onClick={fetchData} className="btn-outline">
            🔄 Actualizar
          </button>
          {!isComprasOnly && (
            <button
              onClick={() => { setShowSupplierModal(true); setShowNewSupplierForm(false); }}
              className="btn-outline"
            >
              🏷️ Proveedores
            </button>
          )}
          <button onClick={() => handleOpenModal()} className="btn-primary">
            ➕ Nueva Compra
          </button>
        </div>
      </div>

      {/* Error Message */}
      {(error || storesError) && (
        <div className="error-message">
          <p>{error || storesError}</p>
          <button onClick={() => { setError(null); setStoresError(null); }} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Summary by Store */}
      {Object.keys(purchasesByStore).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(purchasesByStore).map(([storeId, data]) => {
            const store = stores.find(s => s.id === storeId);
            
            // Skip if store not found (might have been deleted)
            if (!store) {
              console.warn(`Store with ID ${storeId} not found in current stores list`);
              return null;
            }
            
            return (
              <div key={storeId} className="card text-center relative">
                <div className="absolute top-2 right-2 flex space-x-1">
                  <button
                    onClick={() => handleOpenStoreModal(store)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                    title="Editar tienda"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteStore(storeId, data.storeName)}
                    className="text-red-600 hover:text-red-800 text-sm"
                    title="Eliminar tienda"
                  >
                    🗑️
                  </button>
                </div>
                <div className="text-2xl mb-2">🏪</div>
                <h4 className="font-semibold text-gray-900 mb-2 pr-12">{data.storeName}</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Costo:</span> {formatCurrency(data.totalCost)}</p>
                  <p><span className="font-medium">Unidades:</span> {data.totalUnits.toLocaleString()}</p>
                  <p><span className="font-medium">Compras:</span> {data.purchasesCount}</p>
                </div>
                {store && store.location && (
                  <p className="text-xs text-gray-500 mt-1">📍 {store.location}</p>
                )}
              </div>
            );
          }).filter(Boolean)} {/* Filter out null entries */}
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Filtros</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="form-label">Tienda</label>
            <select
              value={filters.storeId}
              onChange={(e) => handleFilterChange('storeId', e.target.value)}
              className="form-select"
            >
              <option value="">Todas las tiendas</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">N° Factura</label>
            <input
              type="text"
              value={searchInvoiceNumber}
              onChange={(e) => handleInvoiceNumberChange(e.target.value)}
              className="form-input"
              placeholder="Buscar por número..."
            />
          </div>
          <div>
            <label className="form-label">Fecha Desde</label>
            <DatePicker
              selected={filters.startDate}
              onChange={(date) => handleFilterChange('startDate', date)}
              className="form-input"
              placeholderText="Seleccionar fecha"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div>
            <label className="form-label">Fecha Hasta</label>
            <DatePicker
              selected={filters.endDate}
              onChange={(date) => handleFilterChange('endDate', date)}
              className="form-input"
              placeholderText="Seleccionar fecha"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                clearFilters();
                setSearchInvoiceNumber('');
                if (invoiceSearchTimer.current) clearTimeout(invoiceSearchTimer.current);
              }}
              className="btn-outline w-full"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      {purchases.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📦</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {Object.values(filters).some(f => f) ? 'No se encontraron compras con estos filtros' : 'No hay compras de inventario registradas'}
          </h3>
          <p className="text-gray-600 mb-4">
            {Object.values(filters).some(f => f) ? 'Intenta ajustar los filtros de búsqueda' : 'Comienza registrando tu primera compra de inventario'}
          </p>
          {!Object.values(filters).some(f => f) && (
            <button onClick={() => handleOpenModal()} className="btn-primary">
              Registrar Primera Compra
            </button>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha Recepción</th>
                  <th>Tienda</th>
                  <th>Proveedor</th>
                  <th>N° Factura</th>
                  <th>Cantidad</th>
                  <th>Monto Total</th>
                  <th>Días Crédito</th>
                  <th>Vencimiento</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pagedPurchases.map((purchase, index) => (
                  <tr key={purchase.id}>
                    <td>
                      <span className="font-semibold text-gray-600">
                        {(page - 1) * PAGE_SIZE + index + 1}
                      </span>
                    </td>
                    <td>{formatDate(purchase.reception_date)}</td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <span className="text-blue-600">🏪</span>
                        <span>{getStoreName(purchase.store_id)}</span>
                      </div>
                    </td>
                    <td>
                      <span className="font-medium text-gray-900">
                        {purchase.supplier}
                      </span>
                    </td>
                    <td>
                      <span className="text-gray-700 font-mono">
                        {purchase.invoice_number}
                      </span>
                    </td>
                    <td>
                      <span className="font-semibold text-blue-600">
                        {purchase.quantity} unidades
                      </span>
                    </td>
                    <td>
                      <span className="font-semibold text-red-600">
                        {formatCurrency(purchase.total_amount_usd)}
                      </span>
                    </td>
                    <td>
                      <span className="text-gray-900">
                        {purchase.credit_days} días
                      </span>
                    </td>
                    <td>
                      <span className={`text-sm ${
                        new Date(purchase.due_date) < new Date() ? 'text-red-600 font-bold' : 'text-gray-700'
                      }`}>
                        {formatDate(purchase.due_date)}
                      </span>
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        {!isComprasOnly && (
                          <>
                            <button
                              onClick={() => handleOpenModal(purchase)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                              title="Editar compra"
                            >
                              ✏️ Editar
                            </button>
                            <button
                              onClick={() => handleDelete(purchase.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                              title="Eliminar compra"
                            >
                              🗑️ Eliminar
                            </button>
                          </>
                        )}
                        {isComprasOnly && <span className="text-gray-400 text-sm">-</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '12px 0 4px' }}>
              <button onClick={() => setPage(1)} disabled={page === 1} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', cursor: page === 1 ? 'default' : 'pointer', color: page === 1 ? '#cbd5e1' : '#475569' }}>«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', cursor: page === 1 ? 'default' : 'pointer', color: page === 1 ? '#cbd5e1' : '#475569' }}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2).reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, []).map((p, i) => (
                typeof p === 'string'
                  ? <span key={`e${i}`} style={{ color: '#94a3b8', padding: '0 4px' }}>…</span>
                  : <button key={p} onClick={() => setPage(p)} style={{ background: page === p ? '#6366f1' : 'none', color: page === p ? '#fff' : '#475569', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: page === p ? 700 : 400 }}>{p}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', cursor: page === totalPages ? 'default' : 'pointer', color: page === totalPages ? '#cbd5e1' : '#475569' }}>›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', cursor: page === totalPages ? 'default' : 'pointer', color: page === totalPages ? '#cbd5e1' : '#475569' }}>»</button>
              <span style={{ marginLeft: 8, fontSize: 12, color: '#94a3b8' }}>Página {page} de {totalPages} ({filteredPurchases.length} registros)</span>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingPurchase ? 'Editar Compra de Inventario' : 'Nueva Compra de Inventario'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body space-y-4">
                <div className="form-group">
                  <label className="form-label">Tienda *</label>
                  <select
                    name="store_id"
                    value={formData.store_id}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">Seleccionar tienda</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Proveedor *</label>
                  <div className="flex items-center gap-2">
                    <select
                      name="supplier"
                      value={formData.supplier}
                      onChange={handleInputChange}
                      className="form-select flex-1"
                      required
                    >
                      <option value="">— Seleccionar proveedor —</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => { setShowSupplierModal(true); setShowNewSupplierForm(true); }}
                      className="btn-outline text-sm px-3 py-2 whitespace-nowrap"
                      title="Agregar nuevo proveedor al catálogo"
                    >
                      ＋ Nuevo
                    </button>
                  </div>
                  {!isComprasOnly && (
                    <button
                      type="button"
                      onClick={() => { setShowSupplierModal(true); setShowNewSupplierForm(false); }}
                      className="text-sm text-blue-600 hover:text-blue-800 mt-1"
                    >
                      🔧 Gestionar Proveedores
                    </button>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Número de Factura *</label>
                  <input
                    type="text"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Ej: FAC-001234"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label text-lg font-semibold">Cantidad de Unidades *</label>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      className="form-input text-xl font-bold h-12 border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-200 text-center"
                      placeholder="100"
                      min="1"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label text-lg font-semibold">Días de Crédito *</label>
                    <input
                      type="number"
                      name="credit_days"
                      value={formData.credit_days}
                      onChange={handleInputChange}
                      className="form-input text-xl font-bold h-12 border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-200 text-center"
                      placeholder="30"
                      min="0"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Número de días para el vencimiento de la factura
                    </p>
                  </div>
                </div>

                {/* Dual Currency Fields with Invoice Rate */}
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-4 text-lg">💰 Montos de la Factura</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="form-group">
                      <label className="form-label text-lg font-semibold text-blue-800">Monto Total USD ($) *</label>
                      <input
                        type="number"
                        value={formData.total_amount_usd}
                        onChange={(e) => handleUSDAmountChange(e.target.value)}
                        className="form-input text-2xl font-bold h-16 border-2 border-green-300 focus:border-green-500 focus:ring-green-200 bg-green-50 text-green-800 text-center"
                        placeholder="1,500.00"
                        min="0"
                        step="0.01"
                        required
                      />
                      <div className="mt-2 p-2 bg-white rounded border">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">En palabras:</span> {formData.total_amount_usd ? 
                            `${parseFloat(formData.total_amount_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} dólares` : 
                            'Ingrese monto USD'
                          }
                        </p>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label text-lg font-semibold text-blue-800">Monto Total VES (Bs.) *</label>
                      <input
                        type="number"
                        value={formData.total_amount_ves_original}
                        onChange={(e) => handleVESAmountChange(e.target.value)}
                        className="form-input text-2xl font-bold h-16 border-2 border-blue-300 focus:border-blue-500 focus:ring-blue-200 bg-blue-50 text-blue-800 text-center"
                        placeholder="58,125.00"
                        min="0"
                        step="0.01"
                        required
                      />
                      <div className="mt-2 p-2 bg-white rounded border">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">En palabras:</span> {formData.total_amount_ves_original ? 
                            `${parseFloat(formData.total_amount_ves_original).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} bolívares` : 
                            'Ingrese monto VES'
                          }
                        </p>
                        <p className="text-xs text-blue-700 mt-1 font-medium bg-blue-100 px-2 py-1 rounded">
                          💎 Monto congelado hasta vencimiento de la factura
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label text-lg font-semibold text-purple-800">Tasa de Cambio de la Factura *</label>
                      <input
                        type="number"
                        value={formData.exchange_rate_at_purchase}
                        onChange={(e) => handleExchangeRateChange(e.target.value)}
                        className="form-input text-xl font-bold h-14 border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-200 bg-purple-50 text-purple-800 text-center"
                        placeholder={exchangeRate.toString()}
                        min="0"
                        step="0.01"
                        required
                      />
                      <p className="text-xs text-green-600 mt-1">
                        📊 Actual: Bs. {exchangeRate.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded border">
                    <h5 className="font-medium text-gray-800 mb-2">🔒 Sistema de Indexación</h5>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Hasta vencimiento:</strong> Monto VES fijo (Bs. {formData.total_amount_ves_original || '0.00'})</p>
                      <p><strong>Después vencimiento:</strong> Monto indexado a tasa actual</p>
                      <p className="text-xs text-blue-500 mt-2">
                        ⚡ Los campos se actualizan automáticamente al modificar cualquiera
                      </p>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label text-lg font-semibold">Fecha de Recepción *</label>
                  <DatePicker
                    selected={formData.reception_date}
                    onChange={(date) => {
                      if (date) {
                        // Normalize the date to prevent timezone issues
                        const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                        setFormData(prev => ({ ...prev, reception_date: normalizedDate }));
                      } else {
                        setFormData(prev => ({ ...prev, reception_date: date }));
                      }
                    }}
                    className="form-input text-lg h-12 border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                    dateFormat="dd/MM/yyyy"
                    required
                  />
                </div>

                {formData.reception_date && formData.credit_days && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-sm text-blue-800">
                      <strong>Fecha de vencimiento calculada:</strong> {' '}
                      {(() => {
                        const vencimiento = new Date(formData.reception_date);
                        vencimiento.setDate(vencimiento.getDate() + parseInt(formData.credit_days || 0));
                        return formatDate(vencimiento);
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      {editingPurchase ? 'Actualizando...' : 'Registrando...'}
                    </span>
                  ) : (
                    editingPurchase ? 'Actualizar Compra' : 'Registrar Compra'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Store Edit Modal */}
      {showStoreModal && editingStore && (
        <div className="modal-overlay" onClick={handleCloseStoreModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">✏️ Editar Tienda</h2>
              <button
                onClick={handleCloseStoreModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleStoreSubmit}>
              <div className="modal-body space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">📝 Editando Tienda</h4>
                  <p className="text-sm text-blue-700">
                    Modifica la información de la tienda. Los cambios se aplicarán a todas las compras de inventario asociadas.
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Nombre de la Tienda *</label>
                  <input
                    type="text"
                    name="name"
                    value={storeFormData.name}
                    onChange={handleStoreInputChange}
                    className="form-input"
                    placeholder="Ej: Tienda Centro"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Ubicación *</label>
                  <input
                    type="text"
                    name="location"
                    value={storeFormData.location}
                    onChange={handleStoreInputChange}
                    className="form-input"
                    placeholder="Ej: Calle Mayor 123, Madrid"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <textarea
                    name="description"
                    value={storeFormData.description}
                    onChange={handleStoreInputChange}
                    className="form-textarea"
                    rows="3"
                    placeholder="Descripción opcional de la tienda..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Meta de Ventas Mensual ($)</label>
                  <input
                    type="number"
                    name="monthly_goal"
                    value={storeFormData.monthly_goal}
                    onChange={handleStoreInputChange}
                    className="form-input"
                    placeholder="10000"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseStoreModal}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Actualizando...
                    </span>
                  ) : (
                    'Actualizar Tienda'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Supplier Management Modal */}
      {showSupplierModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{maxWidth: '750px'}}>
            <div className="modal-header">
              <h2 className="text-xl font-bold">🔧 Catálogo de Proveedores</h2>
              <button onClick={() => {
                setShowSupplierModal(false);
                setSelectedSupplier(null);
                setMergeTarget('');
                setShowNewSupplierForm(false);
                setNewSupplierData({ name: '', aliases: '', credit_days: 0, discount_pct: 0 });
                setNewSupplierError('');
                setEditingAliases(null);
              }} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
            </div>
            <div className="p-6">

              {/* ── Formulario agregar nuevo proveedor ── */}
              {showNewSupplierForm ? (
                <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-3">➕ Agregar Nuevo Proveedor</h3>
                  {newSupplierError && (
                    <p className="text-red-600 text-sm mb-2">{newSupplierError}</p>
                  )}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="col-span-2">
                      <label className="form-label text-sm">Nombre canónico *</label>
                      <input
                        type="text"
                        value={newSupplierData.name}
                        onChange={e => setNewSupplierData(p => ({...p, name: e.target.value}))}
                        className="form-input"
                        placeholder="Ej: DROGUERIA COBECA CENTRO C.A"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="form-label text-sm">Variantes / aliases <span className="text-gray-400">(separados por coma)</span></label>
                      <input
                        type="text"
                        value={newSupplierData.aliases}
                        onChange={e => setNewSupplierData(p => ({...p, aliases: e.target.value}))}
                        className="form-input"
                        placeholder="Ej: DROGUERIA COBECA, COBECA CENTRO"
                      />
                    </div>
                    <div>
                      <label className="form-label text-sm">Días de crédito</label>
                      <input
                        type="number" min="0"
                        value={newSupplierData.credit_days}
                        onChange={e => setNewSupplierData(p => ({...p, credit_days: parseInt(e.target.value)||0}))}
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="form-label text-sm">Descuento pronto pago (%)</label>
                      <input
                        type="number" min="0" step="0.1"
                        value={newSupplierData.discount_pct}
                        onChange={e => setNewSupplierData(p => ({...p, discount_pct: parseFloat(e.target.value)||0}))}
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        setNewSupplierError('');
                        if (!newSupplierData.name.trim()) {
                          setNewSupplierError('El nombre es obligatorio');
                          return;
                        }
                        try {
                          const aliases = newSupplierData.aliases
                            .split(',')
                            .map(a => a.trim())
                            .filter(Boolean);
                          await createSupplier({
                            name: newSupplierData.name.trim(),
                            aliases,
                            credit_days: newSupplierData.credit_days,
                            discount_pct: newSupplierData.discount_pct,
                          });
                          await fetchSuppliers();
                          setShowNewSupplierForm(false);
                          setNewSupplierData({ name: '', aliases: '', credit_days: 0, discount_pct: 0 });
                          // Si el modal se abrió desde el form de factura, pre-seleccionar el nuevo proveedor
                          setFormData(prev => ({...prev, supplier: newSupplierData.name.trim()}));
                        } catch (err) {
                          const msg = err?.response?.data?.detail || err.message;
                          setNewSupplierError(msg);
                        }
                      }}
                      className="btn-primary text-sm"
                    >
                      ✓ Guardar Proveedor
                    </button>
                    <button
                      onClick={() => { setShowNewSupplierForm(false); setNewSupplierError(''); }}
                      className="btn-outline text-sm"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm text-gray-600">
                    Catálogo oficial de proveedores. Fusiona duplicados o elimina entradas sin facturas.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowNewSupplierForm(true); setNewSupplierError(''); }}
                      className="btn-primary text-sm"
                    >
                      ＋ Nuevo Proveedor
                    </button>
                    {!isComprasOnly && (
                      <button
                        disabled={migrating}
                        onClick={async () => {
                          if (!window.confirm('¿Importar todos los nombres actuales de facturas al catálogo?\n\nEsto solo crea entradas faltantes, no elimina nada.')) return;
                          setMigrating(true);
                          try {
                            const res = await migrateSuppliers();
                            await fetchSuppliers();
                            alert(`Migración completada: ${res.created} creados, ${res.skipped} ya existían.`);
                          } catch (err) {
                            alert('Error en migración: ' + (err?.response?.data?.detail || err.message));
                          } finally {
                            setMigrating(false);
                          }
                        }}
                        className="btn-outline text-sm"
                        title="Importar nombres de facturas existentes al catálogo"
                      >
                        {migrating ? '⏳ Migrando…' : '⬆️ Importar del historial'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Lista de proveedores ── */}
              <div className="space-y-2 max-h-[420px] overflow-y-auto">
                {suppliers.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-6">
                    No hay proveedores en el catálogo. Usa "⬆️ Importar del historial" para migrar los existentes o "＋ Nuevo Proveedor" para agregar uno.
                  </p>
                )}
                {suppliers.map(supplier => (
                  <div key={supplier.id} className="border border-gray-200 rounded-lg bg-gray-50">
                    {/* Fila principal */}
                    <div className="flex items-start justify-between p-3">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-gray-900">{supplier.name}</span>
                        {supplier.credit_days > 0 && (
                          <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                            {supplier.credit_days}d
                          </span>
                        )}
                        {supplier.discount_pct > 0 && (
                          <span className="ml-1 text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                            -{supplier.discount_pct}%
                          </span>
                        )}
                        {/* Aliases */}
                        {supplier.aliases && supplier.aliases.length > 0 && editingAliases?.id !== supplier.id && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {supplier.aliases.map((a, i) => (
                              <span key={i} className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">
                                {a}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Editor de aliases */}
                        {editingAliases?.id === supplier.id && (
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              type="text"
                              value={aliasesInput}
                              onChange={e => setAliasesInput(e.target.value)}
                              className="form-input text-sm flex-1"
                              placeholder="alias1, alias2, …"
                            />
                            <button
                              onClick={async () => {
                                const aliases = aliasesInput.split(',').map(a=>a.trim()).filter(Boolean);
                                try {
                                  await updateSupplier(supplier.id, { aliases });
                                  await fetchSuppliers();
                                  setEditingAliases(null);
                                } catch (err) {
                                  alert('Error: ' + (err?.response?.data?.detail || err.message));
                                }
                              }}
                              className="btn-primary text-sm px-3 py-1"
                            >✓</button>
                            <button onClick={() => setEditingAliases(null)} className="btn-outline text-sm px-3 py-1">✕</button>
                          </div>
                        )}
                        {/* Fusionar */}
                        {selectedSupplier?.id === supplier.id && (
                          <div className="mt-2 flex items-center gap-2">
                            <select
                              value={mergeTarget}
                              onChange={e => setMergeTarget(e.target.value)}
                              className="form-select text-sm flex-1"
                            >
                              <option value="">— Fusionar en… —</option>
                              {suppliers.filter(s => s.id !== supplier.id).map(s => (
                                <option key={s.id} value={s.name}>{s.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={async () => {
                                if (!mergeTarget) { alert('Selecciona el proveedor destino'); return; }
                                if (!window.confirm(`¿Fusionar "${supplier.name}" en "${mergeTarget}"?\n\nTodas sus facturas pasarán al proveedor destino y "${supplier.name}" quedará desactivado.`)) return;
                                try {
                                  await mergeSuppliers(supplier.name, mergeTarget);
                                  await fetchSuppliers();
                                  await fetchData();
                                  await fetchData();
                                  setSelectedSupplier(null);
                                  setMergeTarget('');
                                  alert('Fusión exitosa');
                                } catch (err) {
                                  alert('Error: ' + (err?.response?.data?.detail || err.message));
                                }
                              }}
                              className="btn-primary text-sm px-3 py-1"
                            >✓ Fusionar</button>
                            <button onClick={() => { setSelectedSupplier(null); setMergeTarget(''); }} className="btn-outline text-sm px-2 py-1">✕</button>
                          </div>
                        )}
                      </div>

                      {/* Botones de acción */}
                      {selectedSupplier?.id !== supplier.id && editingAliases?.id !== supplier.id && (
                        <div className="flex items-center gap-1 ml-2 shrink-0">
                          <button
                            onClick={() => { setEditingAliases(supplier); setAliasesInput((supplier.aliases||[]).join(', ')); }}
                            className="text-yellow-600 hover:text-yellow-800 text-xs px-2 py-1 border border-yellow-300 rounded"
                            title="Editar variantes / aliases"
                          >🏷️ Aliases</button>
                          <button
                            onClick={() => { setSelectedSupplier(supplier); setMergeTarget(''); }}
                            className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 border border-blue-300 rounded"
                            title="Fusionar con otro proveedor"
                          >🔀 Fusionar</button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`¿Desactivar "${supplier.name}"?\n\nSolo es posible si no tiene facturas asociadas.`)) return;
                              try {
                                await deleteSupplier(supplier.id);
                                await fetchSuppliers();
                              } catch (err) {
                                alert('Error: ' + (err?.response?.data?.detail || err.message));
                              }
                            }}
                            className="text-red-600 hover:text-red-800 text-xs px-2 py-1 border border-red-300 rounded"
                            title="Desactivar proveedor (sin facturas)"
                          >🗑️</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  onClick={() => {
                    setShowSupplierModal(false);
                    setSelectedSupplier(null);
                    setMergeTarget('');
                    setShowNewSupplierForm(false);
                    setEditingAliases(null);
                  }}
                  className="btn-outline"
                >Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;